import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Zap, Users, Brain, Lock, CheckCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const MoatSection = () => {
  const realStrengths = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Truly Automated",
      description: "Set up once and let it run - no daily management needed",
      stat: "2 min setup"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Your Own Mailboxes",
      description: "Dedicated sending infrastructure means 25-45% open rates",
      stat: "25-45%"
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "AI That Works",
      description: "Personalized emails that actually get responses",
      stat: "8.7% reply rate"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Easy Setup",
      description: "Set up once and let it run - no daily management needed",
      stat: "2 min setup"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900 text-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-purple-500/20 text-purple-300 border-purple-400">
            Why Choose Munger
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            The Simplest Way to{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
              Scale Cold Email
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            We handle everything - from finding prospects to booking meetings. 
            You just show up to demos.
          </p>
        </div>

        {/* Strengths Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {realStrengths.map((strength, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
            >
              <div className="text-blue-400 mb-4">{strength.icon}</div>
              <div className="text-2xl font-bold text-white mb-2">{strength.stat}</div>
              <h3 className="text-lg font-semibold text-white mb-2">{strength.title}</h3>
              <p className="text-gray-300 text-sm">{strength.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Key Differentiators */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
          <h3 className="text-2xl font-bold text-white mb-6 text-center">
            What Actually Sets Us Apart
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-400 mb-2">70+</div>
              <div className="text-white font-semibold mb-2">Warmed Domains</div>
              <p className="text-gray-300 text-sm">
                Pre-warmed infrastructure ready to use instantly
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-400 mb-2">250M+</div>
              <div className="text-white font-semibold mb-2">Contact Database</div>
              <p className="text-gray-300 text-sm">
                Find your ideal customers without manual research
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-400 mb-2">Low</div>
              <div className="text-white font-semibold mb-2">Cost Per Meeting</div>
              <p className="text-gray-300 text-sm">
                30x cheaper than hiring SDRs or agencies
              </p>
            </div>
          </div>
        </div>

        {/* Bottom text */}
        <div className="text-center mt-12">
          <p className="text-gray-300 mb-4">
            <CheckCircle className="w-4 h-4 inline mr-2" />
            No contracts • Cancel anytime • Real support when you need it
          </p>
        </div>
      </div>
    </section>
  );
};

export default MoatSection; 